import classNames from 'classnames';
import inflection from 'inflection';
import React, { useEffect, useRef, useState } from 'react';
import Button from '../../components/Button';
import Caret from '../../components/Caret';
import { useQuill } from '../QuillContext';
import useElementSizeSelector from '../utils/useElementSizeSelector';
import styles from './AttachmentContainer.module.scss';
import type { Attachment } from './AttachmentItem';
import AttachmentItem from './AttachmentItem';

const NARROW_LIMIT = parseInt(styles.narrowLimit, 10);
const NORMAL_LIMIT = parseInt(styles.normalLimit, 10);

interface AttachmentContainerProps {
  attachments: Attachment[];
  placeholder: boolean;
  onDeleteAttachment: (source: string) => void;
  isAttachmentUploaded: (attachment: Attachment) => boolean;
  shouldShowUploadingAttachment: (attachment: Attachment) => boolean;
}

const AttachmentContainer = ({
  attachments,
  placeholder = false,
  onDeleteAttachment,
  isAttachmentUploaded,
  shouldShowUploadingAttachment,
}: AttachmentContainerProps) => {
  const { readOnly } = useQuill();
  const visibleAttachments = attachments.filter(
    (attachment) =>
      isAttachmentUploaded(attachment) ||
      shouldShowUploadingAttachment(attachment),
  );
  const count = visibleAttachments.length;

  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const [expanded, setExpanded] = useState(false);
  const previousCount = useRef(count);
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const [setElement, narrow] = useElementSizeSelector(
    ({ width }) => width < 500,
  );
  const limit = narrow ? NARROW_LIMIT : NORMAL_LIMIT;

  const diff = Math.abs(limit - count);

  useEffect(() => {
    if (count <= limit) {
      setExpanded(false);
    } else if (previousCount.current < count) {
      setExpanded(true);
    }
    previousCount.current = count;
  }, [count, limit]);

  // Other collaborators may be uploading
  if (!count) {
    return null;
  }

  return (
    <div ref={setElement} className={styles.container}>
      <div data-no-print className={styles.header}>
        <span className={styles.meta}>
          {count} {inflection.inflect('attachment', count)}
        </span>
        {count > limit && (
          <Button
            variant="link"
            size="micro"
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? (
              <>
                Show {diff} less <Caret direction="up" />
              </>
            ) : (
              <>
                Show {diff} more <Caret direction="down" />
              </>
            )}
          </Button>
        )}
      </div>
      <div
        className={classNames('attachment-grid', styles.attachmentGrid, {
          [styles.expanded]: expanded,
          [styles.narrow]: narrow,
          [styles.placeholder]: placeholder,
        })}
        data-attachment-count={count}
      >
        {visibleAttachments.map((attachment) => (
          <AttachmentItem
            key={attachment.source}
            attachment={attachment}
            readOnly={readOnly}
            onDeleteAttachment={
              onDeleteAttachment &&
              (() => onDeleteAttachment(attachment.source))
            }
            isAttachmentUploaded={isAttachmentUploaded}
          />
        ))}
      </div>
    </div>
  );
};

export default AttachmentContainer;
